import { AIR, CHUNK_SIZE, MESH_BOUNDARY } from '../constants';
import { MatFlag } from './materials';

/**
 * Greedy mesher with per-vertex ambient occlusion.
 *
 * Input: a padded voxel volume ((sx+2) x (sy+2) x (sz+2), layout px + pz*PX + py*PX*PZ, one voxel
 * of neighbor data on every side) and a 256-entry material flag table.
 * Output per vertex: `pos` = [x, y, z, normalIndex] and `mat` = [palette, ao, 0, 0], all Uint8.
 * Normal indices: 0 +x, 1 -x, 2 +y, 3 -y, 4 +z, 5 -z.
 * Faces merge only when palette and all four corner AO values match; the shader adds per-voxel
 * shade variation from world position so merged quads still read voxel-by-voxel.
 */
export interface MeshData {
  pos: Uint8Array;
  mat: Uint8Array;
  index: Uint16Array | Uint32Array;
  vertexCount: number;
}

export interface ChunkMesh {
  opaque: MeshData | null;
  transparent: MeshData | null;
}

class MeshBuilder {
  pos: Uint8Array;
  mat: Uint8Array;
  idx: Uint32Array;
  vc = 0;
  ic = 0;

  constructor(cap = 16384) {
    this.pos = new Uint8Array(cap * 4);
    this.mat = new Uint8Array(cap * 4);
    this.idx = new Uint32Array(cap * 1.5);
  }

  reset(): void {
    this.vc = 0;
    this.ic = 0;
  }

  private grow(): void {
    const np = new Uint8Array(this.pos.length * 2);
    np.set(this.pos);
    this.pos = np;
    const nm = new Uint8Array(this.mat.length * 2);
    nm.set(this.mat);
    this.mat = nm;
    const ni = new Uint32Array(this.idx.length * 2);
    ni.set(this.idx);
    this.idx = ni;
  }

  quad(q: Int32Array, normal: number, pal: number, a0: number, a1: number, a2: number, a3: number, crack = 0): void {
    if ((this.vc + 4) * 4 > this.pos.length) this.grow();
    const v = this.vc;
    const P = this.pos;
    const M = this.mat;
    const aos = [a0, a1, a2, a3];
    for (let k = 0; k < 4; k++) {
      const p = (v + k) * 4;
      P[p] = q[k * 3];
      P[p + 1] = q[k * 3 + 1];
      P[p + 2] = q[k * 3 + 2];
      P[p + 3] = normal;
      M[p] = pal;
      M[p + 1] = aos[k];
      M[p + 2] = crack;
      M[p + 3] = 0;
    }
    const I = this.idx;
    let i = this.ic;
    // Flip the diagonal toward the brighter pair to avoid AO anisotropy artifacts.
    if (a0 + a2 > a1 + a3) {
      I[i++] = v; I[i++] = v + 1; I[i++] = v + 2;
      I[i++] = v; I[i++] = v + 2; I[i++] = v + 3;
    } else {
      I[i++] = v + 1; I[i++] = v + 2; I[i++] = v + 3;
      I[i++] = v + 1; I[i++] = v + 3; I[i++] = v;
    }
    this.ic = i;
    this.vc = v + 4;
  }

  finish(): MeshData | null {
    if (this.vc === 0) return null;
    const index = this.vc > 65535 ? this.idx.slice(0, this.ic) : Uint16Array.from(this.idx.subarray(0, this.ic));
    return {
      pos: this.pos.slice(0, this.vc * 4),
      mat: this.mat.slice(0, this.vc * 4),
      index,
      vertexCount: this.vc,
    };
  }
}

const opaqueBuilder = new MeshBuilder();
const transparentBuilder = new MeshBuilder();
let mask = new Int32Array(64 * 64);
const quadScratch = new Int32Array(12);

/** Meshes a padded volume of interior size sx x sy x sz (each <= 255). */
export function meshPadded(vox: Uint8Array, sx: number, sy: number, sz: number, flags: Uint8Array, dmg?: Uint8Array | null): ChunkMesh {
  opaqueBuilder.reset();
  transparentBuilder.reset();
  const PX = sx + 2;
  const PZ = sz + 2;
  const PXZ = PX * PZ;
  const dims = [sx, sy, sz];
  // Strides in the padded array for +1 along x, y, z.
  const stride = [1, PXZ, PX];
  const maxArea = Math.max(sx * sy, sy * sz, sx * sz);
  if (mask.length < maxArea) mask = new Int32Array(maxArea);

  const isOpaque = (v: number): number => (v !== AIR && v !== MESH_BOUNDARY && (flags[v] & MatFlag.Transparent) === 0 ? 1 : 0);

  for (let d = 0; d < 3; d++) {
    const u = (d + 1) % 3;
    const w = (d + 2) % 3;
    const nd = dims[d];
    const nu = dims[u];
    const nw = dims[w];
    const sd = stride[d];
    const su = stride[u];
    const sw = stride[w];
    for (let side = 0; side < 2; side++) {
      const s = side === 0 ? 1 : -1;
      const normal = d * 2 + side;
      const front = sd * s;
      for (let slice = 0; slice < nd; slice++) {
        let any = false;
        for (let j = 0; j < nw; j++) {
          for (let i = 0; i < nu; i++) {
            // Padded index of voxel (slice, i, j) along axes (d, u, w).
            const base = (slice + 1) * sd + (i + 1) * su + (j + 1) * sw;
            const v = vox[base];
            let key = 0;
            if (v !== AIR && v !== MESH_BOUNDARY) {
              const nb = vox[base + front];
              const nbTrans = nb !== AIR && (flags[nb] & MatFlag.Transparent) !== 0;
              if (nb === AIR || (nbTrans && nb !== v)) {
                const vTrans = (flags[v] & MatFlag.Transparent) !== 0;
                const f = base + front;
                const sU0 = isOpaque(vox[f - su]);
                const sU1 = isOpaque(vox[f + su]);
                const sW0 = isOpaque(vox[f - sw]);
                const sW1 = isOpaque(vox[f + sw]);
                const a0 = sU0 && sW0 ? 0 : 3 - (sU0 + sW0 + isOpaque(vox[f - su - sw]));
                const a1 = sU1 && sW0 ? 0 : 3 - (sU1 + sW0 + isOpaque(vox[f + su - sw]));
                const a2 = sU1 && sW1 ? 0 : 3 - (sU1 + sW1 + isOpaque(vox[f + su + sw]));
                const a3 = sU0 && sW1 ? 0 : 3 - (sU0 + sW1 + isOpaque(vox[f - su + sw]));
                key = v | (a0 << 8) | (a1 << 10) | (a2 << 12) | (a3 << 14) | (vTrans ? 1 << 16 : 0) | (dmg ? dmg[base] << 17 : 0);
                any = true;
              }
            }
            mask[i + j * nu] = key;
          }
        }
        if (!any) continue;

        const plane = slice + (s > 0 ? 1 : 0);
        for (let j = 0; j < nw; j++) {
          for (let i = 0; i < nu; ) {
            const key = mask[i + j * nu];
            if (key === 0) {
              i++;
              continue;
            }
            let width = 1;
            while (i + width < nu && mask[i + width + j * nu] === key) width++;
            let height = 1;
            outer: while (j + height < nw) {
              const row = (j + height) * nu;
              for (let k = 0; k < width; k++) if (mask[i + k + row] !== key) break outer;
              height++;
            }
            for (let hh = 0; hh < height; hh++) {
              const row = (j + hh) * nu;
              for (let k = 0; k < width; k++) mask[i + k + row] = 0;
            }

            const pal = key & 0xff;
            const a0 = (key >> 8) & 3;
            const a1 = (key >> 10) & 3;
            const a2 = (key >> 12) & 3;
            const a3 = (key >> 14) & 3;
            const builder = (key >> 16) & 1 ? transparentBuilder : opaqueBuilder;
            const crack = (key >> 17) & 3;
            // Corners in (u, w): c0 (i, j), c1 (i+width, j), c2 (i+width, j+height), c3 (i, j+height).
            const q = quadScratch;
            const cu = [i, i + width, i + width, i];
            const cw = [j, j, j + height, j + height];
            const order = s > 0 ? [0, 1, 2, 3] : [0, 3, 2, 1];
            for (let k = 0; k < 4; k++) {
              const ck = order[k];
              q[k * 3 + d] = plane;
              q[k * 3 + u] = cu[ck];
              q[k * 3 + w] = cw[ck];
            }
            if (s > 0) builder.quad(q, normal, pal, a0, a1, a2, a3, crack);
            else builder.quad(q, normal, pal, a0, a3, a2, a1, crack);
            i += width;
          }
        }
      }
    }
  }
  return { opaque: opaqueBuilder.finish(), transparent: transparentBuilder.finish() };
}

/** Meshes a world chunk copied with VoxelWorld.copyPadded (+ optional crack stages). */
export function meshChunk(vox: Uint8Array, flags: Uint8Array, dmg?: Uint8Array | null): ChunkMesh {
  return meshPadded(vox, CHUNK_SIZE, CHUNK_SIZE, CHUNK_SIZE, flags, dmg);
}

/**
 * Meshes a small standalone grid (characters, weapons, falling islands).
 * Grid layout: x + z * sx + y * sx * sz. Each dimension must be <= 253.
 */
export function meshGrid(grid: Uint8Array, sx: number, sy: number, sz: number, flags: Uint8Array): ChunkMesh {
  const PX = sx + 2;
  const PZ = sz + 2;
  const padded = new Uint8Array(PX * PZ * (sy + 2));
  for (let y = 0; y < sy; y++) {
    for (let z = 0; z < sz; z++) {
      const src = z * sx + y * sx * sz;
      const dst = 1 + (z + 1) * PX + (y + 1) * PX * PZ;
      padded.set(grid.subarray(src, src + sx), dst);
    }
  }
  return meshPadded(padded, sx, sy, sz, flags);
}
